/**
 * DTN Map Tile API Proxy Client
 * All requests route through the server-side proxy to keep credentials secure
 * Works with both Vite dev server proxy and Apache CGI proxy
 */

import {
  DTNCatalog,
  DTNMetadata,
  DTNTimestampWrapper,
  DTNLegend,
  DTNStyle,
} from './types';

// Proxy endpoint base path
const DTN_PROXY_BASE = '/dtn-proxy';

/**
 * Make a request through the proxy
 */
async function proxyFetch<T>(endpoint: string, locale?: string): Promise<T> {
  const headers: Record<string, string> = {
    'Accept': 'application/json',
  };
  
  if (locale) {
    headers['Accept-Language'] = locale;
  }

  const response = await fetch(`${DTN_PROXY_BASE}${endpoint}`, {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`DTN API Error: ${response.status} - ${errorText}`);
  }

  return response.json();
}

/**
 * Get the catalog of available layers
 */
export async function getProxiedDTNCatalog(locale?: string): Promise<DTNCatalog> {
  const params = new URLSearchParams();
  if (locale) params.set('locale', locale);
  const query = params.toString() ? `?${params.toString()}` : '';
  return proxyFetch<DTNCatalog>(`/catalog${query}`, locale);
}

/**
 * Get metadata for a specific layer
 */
export async function getProxiedDTNLayerMetadata(
  layerId: string,
  locale?: string
): Promise<DTNMetadata> {
  const params = new URLSearchParams();
  if (locale) params.set('locale', locale);
  const query = params.toString() ? `?${params.toString()}` : '';
  return proxyFetch<DTNMetadata>(`/metadata/${layerId}${query}`, locale);
}

/**
 * Get timestamps for a layer (returns available tile sets)
 */
export async function getProxiedDTNLayerTimestamps(
  layerId: string,
  timeRange?: string,
  forceOffInterval?: boolean
): Promise<DTNTimestampWrapper[]> {
  const params = new URLSearchParams();
  if (timeRange) params.set('time', timeRange);
  if (forceOffInterval) params.set('forceOffInterval', 'true');
  const query = params.toString() ? `?${params.toString()}` : '';
  return proxyFetch<DTNTimestampWrapper[]>(`/timestamps/${layerId}${query}`);
}

/**
 * Get styles for a layer
 */
export async function getProxiedDTNLayerStyles(
  layerId: string,
  locale?: string
): Promise<DTNStyle[]> {
  const params = new URLSearchParams();
  if (locale) params.set('locale', locale);
  const query = params.toString() ? `?${params.toString()}` : '';
  return proxyFetch<DTNStyle[]>(`/styles/${layerId}${query}`, locale);
}

/**
 * Get legend for a layer
 */
export async function getProxiedDTNLayerLegend(
  layerId: string,
  unit?: string,
  locale?: string
): Promise<DTNLegend[]> {
  const params = new URLSearchParams();
  if (unit) params.set('unit', unit);
  if (locale) params.set('locale', locale);
  const query = params.toString() ? `?${params.toString()}` : '';
  return proxyFetch<DTNLegend[]>(`/legends/${layerId}${query}`, locale);
}

/**
 * Build a tile URL template that routes through the proxy
 * The {z}, {x}, {y} placeholders will be replaced by OpenLayers
 */
export function buildProxiedDTNTileUrlTemplate(
  layerId: string,
  tileSetId: string,
  size: 256 | 512 = 256
): string {
  const queryParams = new URLSearchParams({
    size: size.toString(),
  });

  return `${DTN_PROXY_BASE}/tiles/${layerId}/${tileSetId}/{z}/{x}/{y}.png?${queryParams.toString()}`;
}

/**
 * Check if the DTN proxy is available
 */
export async function checkDTNProxyStatus(): Promise<boolean> {
  try {
    // Try to fetch catalog - if it works, proxy is available
    const response = await fetch(`${DTN_PROXY_BASE}/catalog`, {
      method: 'GET',
      headers: { 'Accept': 'application/json' },
    });
    return response.ok;
  } catch {
    return false;
  }
}

// Alias for backwards compatibility
export { buildProxiedDTNTileUrlTemplate as buildDTNTileUrlTemplate };
